import React, { useState, useEffect } from 'react';
import { X, CreditCard, QrCode, Copy, Check, ArrowRight, Loader2, Lock, Truck, MapPin, PlusCircle, Search, MessageCircle, DollarSign } from 'lucide-react';
import { CartItem } from '../types';
import { API_BASE_URL } from '../constants';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  total: number;
  shippingCost?: number;
  selectedShipping?: any;
  appliedCoupon?: any; // [NOVO] Cupom aplicado
  tenantId: string;
  onClearCart: () => void;
  storePhone?: string;
  showToast?: (message: string, type?: 'success' | 'error' | 'info') => void;
  storeConfig?: any;
}

type CheckoutStep = 'data' | 'address' | 'payment' | 'pix_result' | 'delivery_success';

interface FormData {
  name: string;
  email: string;
  cpf: string;
  phone: string;
  zipCode: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen, onClose, items, total, shippingCost = 0, selectedShipping, appliedCoupon, tenantId, onClearCart, storePhone, showToast, storeConfig
}) => {
  if (!isOpen) return null;

  const [step, setStep] = useState<CheckoutStep>('data');
  const [loading, setLoading] = useState(false);
  const [isCustomerFound, setIsCustomerFound] = useState(false);
  const [calculatingZip, setCalculatingZip] = useState(false);
  const [showDeliveryOptions, setShowDeliveryOptions] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState<'credit' | 'debit' | 'pix' | 'cash' | null>(null);
  const [orderFinalAmount, setOrderFinalAmount] = useState<number | null>(null);

  const [formData, setFormData] = useState<FormData>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('@ZapShop:customerData');
        if (saved) return JSON.parse(saved);
      } catch (e) {
        console.error("Falha ao carregar os dados salvos do cliente", e);
      }
    }
    return {
      name: '', email: '', cpf: '', phone: '',
      zipCode: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: ''
    };
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('@ZapShop:customerData', JSON.stringify(formData));
    }
  }, [formData]);

  const [showNewAddressForm, setShowNewAddressForm] = useState(() => {
    return !(formData.street && formData.number);
  });

  // --- AUTO-IDENTIFICAÇÃO (Auto-ID) ---
  useEffect(() => {
    const fetchCustomerData = async () => {
      const params = new URLSearchParams(window.location.search);
      const urlPhone = params.get('client_phone');
      const urlName = params.get('client_name');

      if (urlPhone && isOpen) {
        setLoading(true);
        try {
          const res = await fetch(`${API_BASE_URL}/customers/lookup?tenantId=${tenantId}&phone=${urlPhone}`);
          const data = await res.json();

          if (data.found && data.data) {
            setIsCustomerFound(true);
            const dbData = data.data;
            setFormData(prev => ({
              ...prev,
              name: dbData.name || urlName || prev.name,
              email: dbData.email || prev.email,
              cpf: dbData.cpf || prev.cpf,
              phone: dbData.phone || urlPhone,
              zipCode: dbData.address_zip_code || prev.zipCode,
              street: dbData.address_street || prev.street,
              number: dbData.address_number || prev.number,
              complement: dbData.address_complement || prev.complement,
              city: dbData.address_city || prev.city,
              state: dbData.address_state || prev.state
            }));

            // Se o cliente já tem endereço salvo, fecha o formulário expandido
            if (dbData.address_street && dbData.address_number) {
              setShowNewAddressForm(false);
            }
          } else {
            // Lead não encontrado, preenche o básico do URL
            setFormData(prev => ({ ...prev, name: urlName || prev.name, phone: urlPhone || prev.phone }));
          }
        } catch (err) {
          console.error("Erro na busca rápida:", err);
        } finally {
          setLoading(false);
          setStep('data');
        }
      }
    };
    fetchCustomerData();
  }, [isOpen, tenantId]);

  const [pixData, setPixData] = useState<{ qr_code: string; qr_code_base64: string } | null>(null);
  const [copied, setCopied] = useState(false);

  // --- CUPOM (NOVO) ---
  const [couponCode, setCouponCode] = useState('');
  const [localAppliedCoupon, setLocalAppliedCoupon] = useState<any>(appliedCoupon || null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidatingCoupon(true);
    try {
      const res = await fetch(`${API_BASE_URL}/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantId,
          code: couponCode,
          cart_total: total,
          items: items.map(i => ({
            product_id: i.id.split('--')[0],
            price: i.price,
            quantity: i.quantity
          }))
        })
      });
      const data = await res.json();
      if (data.valid) {
        setLocalAppliedCoupon(data.coupon);
        showToast ? showToast("Cupom aplicado!", "success") : alert("Cupom aplicado!");
      } else {
        showToast ? showToast(data.message || "Cupom inválido", "error") : alert(data.message || "Cupom inválido");
      }
    } catch (err) {
      console.error("Erro ao validar cupom:", err);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const calculateDiscount = () => {
    if (!localAppliedCoupon) return 0;
    // O backend já retornou o eligibleSubtotal
    const eligibleSubtotal = localAppliedCoupon.eligible_subtotal || total;
    if (localAppliedCoupon.discount_type === 'PERCENTAGE') {
      return (eligibleSubtotal * parseFloat(localAppliedCoupon.discount_value)) / 100;
    }
    return Math.min(parseFloat(localAppliedCoupon.discount_value), eligibleSubtotal);
  };

  const discount = calculateDiscount();
  const finalAmount = Math.max(0, total + shippingCost - discount);

  // --- MÁSCARAS ---
  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let v = e.target.value.replace(/\D/g, '');
    if (v.length > 11) v = v.slice(0, 11);
    v = v.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    setFormData({ ...formData, cpf: v });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let v = e.target.value.replace(/\D/g, '');
    v = v.replace(/^(\d{2})(\d)/g, '($1) $2').replace(/(\d)(\d{4})$/, '$1-$2');
    setFormData({ ...formData, phone: v });
  };

  const handleCepChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let v = e.target.value.replace(/\D/g, '');
    if (v.length > 8) v = v.slice(0, 8);
    const maskedV = v.replace(/(\d{5})(\d)/, '$1-$2');
    setFormData({ ...formData, zipCode: maskedV });

    if (v.length === 8) {
      setCalculatingZip(true);
      try {
        const res = await fetch(`https://viacep.com.br/ws/${v}/json/`);
        const data = await res.json();
        if (!data.erro) {
          setFormData(prev => ({
            ...prev,
            zipCode: maskedV,
            street: data.logradouro,
            neighborhood: data.bairro,
            city: data.localidade,
            state: data.uf
          }));
        }
      } catch { } finally {
        setCalculatingZip(false);
      }
    }
  };

  const searchCustomerByPhone = async (phoneToSearch: string) => {
    const cleanPhone = phoneToSearch.replace(/\D/g, '');
    if (cleanPhone.length < 10) return; // Só busca se for um número válido (com DDD)

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/customers/lookup?tenantId=${tenantId}&phone=${cleanPhone}`);
      const data = await res.json();

      if (data.found && data.data) {
        setIsCustomerFound(true);
        const dbData = data.data;
        setFormData(prev => ({
          ...prev,
          name: dbData.name || prev.name,
          email: dbData.email || prev.email,
          cpf: dbData.cpf || prev.cpf,
          zipCode: dbData.address_zip_code || prev.zipCode,
          street: dbData.address_street || prev.street,
          number: dbData.address_number || prev.number,
          complement: dbData.address_complement || prev.complement,
          neighborhood: dbData.address_neighborhood || prev.neighborhood,
          city: dbData.address_city || prev.city,
          state: dbData.address_state || prev.state
        }));

        if (dbData.address_street && dbData.address_number) {
          setShowNewAddressForm(false);
        }
      } else {
        // Se não achou, permite que o cliente edite normalmente
        setIsCustomerFound(false);
      }
    } catch (error) {
      console.error("Erro ao buscar cliente:", error);
    } finally {
      setLoading(false);
    }
  };

  // --- LÓGICA DE PEDIDO ---
  const createOrder = async (payment_method?: string) => {
    const isPickup = selectedShipping?.service_id === 'PICKUP';
    const shipping_type = isPickup ? 'PICKUP' : 'DELIVERY';
    const shipping_address = isPickup ? null : {
      street: formData.street,
      number: formData.number,
      complement: formData.complement,
      neighborhood: formData.neighborhood,
      city: formData.city,
      state: formData.state,
      zipcode: formData.zipCode
    };

    const res = await fetch(`${API_BASE_URL}/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tenant_id: tenantId,
        total_amount: finalAmount,
        shipping_cost: shippingCost,
        shipping_method: selectedShipping ? selectedShipping.name : 'DIGITAL',
        shipping_type,
        shipping_address,
        shipping_details: selectedShipping ? {
          service_id: selectedShipping.service_id,
          name: selectedShipping.name,
          price: selectedShipping.price,
          days: selectedShipping.days
        } : null,
        coupon_code: localAppliedCoupon?.code || null,
        discount_amount: discount,
        customer: {
          name: formData.name,
          email: formData.email,
          cpf: formData.cpf,
          phone: formData.phone,
          address_zip_code: formData.zipCode,
          address_street: formData.street,
          address_number: formData.number,
          address_complement: formData.complement,
          address_neighborhood: formData.neighborhood,
          address_city: formData.city,
          address_state: formData.state
        },
        items: items.map(i => ({
          product_id: i.id.split('--')[0],
          quantity: i.quantity,
          price: i.price,
          variations: i.selected_options
        })),
        payment_method: payment_method || null
      }),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => null);
      throw new Error(errorData?.error || 'Erro ao criar pedido');
    }

    return await res.json();
  };

  const handlePayment = async (method: 'pix' | 'credit_card' | 'delivery', deliverySubMethod?: string) => {
    setLoading(true);
    try {
      setOrderFinalAmount(finalAmount); // Salva o valor antes de limpar o carrinho
      let paymentMethodLabel = method === 'delivery' ? `DELIVERY_${deliverySubMethod?.toUpperCase()}` : undefined;
      const order = await createOrder(paymentMethodLabel);
      if (method === 'delivery') {
        setStep('delivery_success');
        setDeliveryMethod(deliverySubMethod as any);
        onClearCart();
      }
      else if (method === 'pix') {
        const res = await fetch(`${API_BASE_URL}/checkout/process-pix`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenant_id: tenantId,
            order_id: order.id,
            transaction_amount: finalAmount,
            description: `Pedido #${order.id}`,
            payer: { email: formData.email, name: formData.name, cpf: formData.cpf.replace(/\D/g, '') }
          })
        });
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        setPixData(data);
        setStep('pix_result');
      }
      else if (method === 'credit_card') {
        const paymentItems = items.map(i => ({ name: i.name, quantity: i.quantity, price: i.price }));
        if (shippingCost > 0) paymentItems.push({ name: 'Frete de Envio', quantity: 1, price: shippingCost });
        const res = await fetch(`${API_BASE_URL}/checkout/create-preference`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenant_id: tenantId,
            order_id: order.id,
            items: paymentItems,
            payer_email: formData.email
          })
        });
        const data = await res.json();
        if (data.init_point) window.location.href = data.init_point;
      }
    } catch (err) {
      alert('Erro ao processar: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (pixData?.qr_code) {
      navigator.clipboard.writeText(pixData.qr_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNextStep = () => {
    if (step === 'data') {
      if (selectedShipping) setStep('address');
      else setStep('payment');
    } else if (step === 'address') {
      if (!formData.street || !formData.number || !formData.city) {
        showToast ? showToast("Preencha o endereço completo.", "error") : alert("Preencha o endereço completo.");
        return;
      }
      setStep('payment');
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity" onClick={onClose} />

      <div className="relative bg-white w-full max-w-lg h-[90vh] sm:h-auto sm:max-h-[90vh] sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col overflow-hidden animate-slide-up">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white shrink-0 relative">
          <h2 className="text-xl font-bold text-gray-800">Finalizar Compra</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full text-gray-500"><X size={24} /></button>

          {/* BARRA DE PROGRESSO (NOVO) */}
          <div className="absolute bottom-0 left-0 h-1 bg-gray-100 w-full overflow-hidden">
            <div
              className="h-full transition-all duration-500 ease-out"
              style={{
                backgroundColor: '#10b981',
                width: step === 'data' ? '25%' : step === 'address' ? '50%' : step === 'payment' ? '75%' : '100%'
              }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">

          {loading && step === 'data' ? (
            <div className="flex flex-col items-center justify-center py-10 text-emerald-600">
              <Loader2 className="animate-spin mb-4" size={32} />
              <p className="text-sm font-bold">Buscando seu cadastro...</p>
            </div>
          ) : (
            <>
              {/* ETAPA 1: DADOS PESSOAIS */}
              {step === 'data' && (
                <div className="space-y-4 animate-fade-in">
                  <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mb-6 flex justify-between items-center shadow-sm">
                    <p className="text-sm text-blue-700 flex items-center gap-2">
                      <Lock size={16} /> Compra 100% Segura
                    </p>
                    {isCustomerFound && <span className="text-[10px] bg-blue-200 text-blue-800 px-2 py-1 rounded-full font-bold uppercase">Cliente ID</span>}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">WhatsApp / Celular</label>
                    <p className="text-xs text-emerald-600 font-medium mb-2 -mt-1">Se já é nosso cliente, insira seu celular</p>
                    <div className="relative">
                      <input type="tel" className="input-primary w-full" placeholder="(00) 00000-0000" value={formData.phone} onChange={handlePhoneChange} onBlur={(e) => searchCustomerByPhone(e.target.value)} maxLength={15} />
                      {isCustomerFound && <Check size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />}
                    </div>
                  </div>

                  <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome Completo</label><input type="text" className="input-primary" placeholder="Seu nome" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} /></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">CPF</label><input type="text" className="input-primary" placeholder="000.000.000-00" value={formData.cpf} onChange={handleCpfChange} maxLength={14} /></div>
                    <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">E-mail</label><input type="email" className="input-primary" placeholder="email@exemplo.com" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} /></div>
                  </div>

                  <button onClick={handleNextStep} disabled={!formData.name || !formData.cpf || !formData.email || formData.cpf.length < 14} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-xl mt-6 flex items-center justify-center gap-2 disabled:opacity-50 transition-all shadow-lg shadow-emerald-200">
                    Continuar <ArrowRight size={20} />
                  </button>
                </div>
              )}

              {/* ETAPA 2: ENDEREÇO COM ACCORDION E CARD */}
              {step === 'address' && (
                <div className="space-y-4 animate-fade-in">
                  <h3 className="font-bold text-gray-800 flex items-center gap-2 mb-4"><Truck size={20} className="text-orange-600" /> Endereço de Entrega</h3>

                  {/* Card de Endereço Salvo */}
                  {!showNewAddressForm && formData.street && (
                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.08)] relative overflow-hidden group mb-6">
                      <div className="absolute top-0 left-0 w-1 h-full bg-orange-500"></div>
                      <div className="flex gap-4 items-start">
                        <div className="w-10 h-10 bg-orange-50 rounded-full flex items-center justify-center shrink-0">
                          <MapPin size={20} className="text-orange-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 text-sm mb-1">{formData.street}, {formData.number}</h4>
                          <p className="text-xs text-gray-500 mb-1">{formData.neighborhood} - {formData.city}/{formData.state}</p>
                          <p className="text-xs font-medium text-gray-400">CEP: {formData.zipCode}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Botão de Expansão Sutil */}
                  {!showNewAddressForm && (
                    <button
                      onClick={() => { setShowNewAddressForm(true); setFormData(prev => ({ ...prev, zipCode: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: '' })); }}
                      className="w-full py-3 flex items-center justify-center gap-2 text-sm font-bold text-orange-600 hover:bg-orange-50 rounded-xl border border-dashed border-orange-200 transition-colors"
                    >
                      <PlusCircle size={18} /> Novo Endereço de Entrega
                    </button>
                  )}

                  {/* Formulário Expandido (Minimalista) */}
                  {showNewAddressForm && (
                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm animate-slide-up">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">CEP</label>
                          <div className="relative">
                            <input className="input-primary w-full" placeholder="00000-000" value={formData.zipCode} onChange={handleCepChange} maxLength={9} />
                            {calculatingZip && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />}
                          </div>
                        </div>
                        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Número</label><input className="input-primary" placeholder="123" value={formData.number} onChange={e => setFormData({ ...formData, number: e.target.value })} /></div>
                      </div>

                      <div className={`transition-all duration-500 overflow-hidden ${(formData.zipCode.length >= 8 || formData.street) ? 'max-h-[500px] opacity-100 mt-4 space-y-4' : 'max-h-0 opacity-0'}`}>
                        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Endereço</label><input className="input-primary" placeholder="Rua..." value={formData.street} onChange={e => setFormData({ ...formData, street: e.target.value })} /></div>
                        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Complemento</label><input className="input-primary" placeholder="Apto, Bloco..." value={formData.complement} onChange={e => setFormData({ ...formData, complement: e.target.value })} /></div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Bairro</label><input className="input-primary" value={formData.neighborhood} onChange={e => setFormData({ ...formData, neighborhood: e.target.value })} /></div>
                          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Cidade/UF</label><input className="input-primary bg-gray-50 text-gray-500 border-dashed" readOnly value={formData.city ? `${formData.city}/${formData.state}` : ''} /></div>
                        </div>
                      </div>
                    </div>
                  )}

                  <button onClick={() => setStep('payment')} disabled={!formData.street || !formData.number} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-xl mt-6 flex items-center justify-center gap-2 transition-all shadow-lg shadow-emerald-200 disabled:opacity-50">
                    Ir para Pagamento <ArrowRight size={20} />
                  </button>
                </div>
              )}

              {/* ETAPA 3: PAGAMENTO */}
              {step === 'payment' && (
                <div className="space-y-4 animate-fade-in">
                  {/* CUPOM DE DESCONTO (NOVO) */}
                  {!localAppliedCoupon ? (
                    <div className="bg-white p-4 rounded-xl border border-dashed border-gray-300 shadow-sm">
                      <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Cupom de Desconto</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          className="input-primary flex-1 !py-2 text-sm uppercase"
                          placeholder="EX: BEMVINDO10"
                          value={couponCode}
                          onChange={e => setCouponCode(e.target.value.toUpperCase())}
                        />
                        <button
                          onClick={handleApplyCoupon}
                          disabled={validatingCoupon || !couponCode}
                          className="bg-gray-900 text-white px-4 py-2 rounded-lg text-xs font-bold disabled:opacity-50 transition-all hover:bg-black active:scale-95"
                        >
                          {validatingCoupon ? '...' : 'Aplicar'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-emerald-50 p-3 rounded-xl border border-emerald-200 flex justify-between items-center animate-fade-in">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-emerald-500 text-white rounded-lg flex items-center justify-center shadow-sm">
                          <Check size={16} strokeWidth={3} />
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-emerald-800 uppercase leading-none mb-1">Cupom Aplicado</p>
                          <p className="text-sm font-bold text-emerald-700">{localAppliedCoupon.code}</p>
                        </div>
                      </div>
                      <button onClick={() => { setLocalAppliedCoupon(null); setCouponCode(''); }} className="text-xs font-bold text-emerald-600 hover:text-emerald-800 underline">Remover</button>
                    </div>
                  )}

                  <div className="bg-white p-4 rounded-xl border border-gray-200 mb-4 shadow-sm">
                    <div className="flex justify-between text-sm text-gray-500 mb-1"><span>Produtos</span><span>R$ {total.toFixed(2)}</span></div>
                    {shippingCost > 0 && <div className="flex justify-between text-sm text-orange-600 mb-1"><span>Frete ({selectedShipping?.name})</span><span>+ R$ {shippingCost.toFixed(2)}</span></div>}
                    {discount > 0 && <div className="flex justify-between text-sm text-emerald-600 mb-1"><span>Desconto</span><span>- R$ {discount.toFixed(2)}</span></div>}
                    <div className="flex justify-between font-bold text-lg text-gray-900 border-t pt-2 mt-2"><span>Total a Pagar</span><span>R$ {finalAmount.toFixed(2)}</span></div>
                  </div>

                  {!storeConfig?.disable_automated_payments && (
                    <>
                      <button onClick={() => handlePayment('pix')} disabled={loading} className="w-full bg-white border-2 border-emerald-100 hover:border-emerald-500 p-4 rounded-xl flex items-center justify-between group transition-all shadow-sm">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-600"><QrCode size={24} /></div>
                          <div className="text-left"><h3 className="font-bold text-gray-800">Pix (Instantâneo)</h3><p className="text-xs text-emerald-600 font-medium">Aprovação imediata</p></div>
                        </div>
                        <ArrowRight className="text-gray-300 group-hover:text-emerald-500" />
                      </button>

                      <button onClick={() => handlePayment('credit_card')} disabled={loading} className="w-full bg-white border-2 border-gray-100 hover:border-blue-500 p-4 rounded-xl flex items-center justify-between group transition-all shadow-sm">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-blue-600"><CreditCard size={24} /></div>
                          <div className="text-left"><h3 className="font-bold text-gray-800">Cartão de Crédito</h3><p className="text-xs text-gray-500">Até 12x</p></div>
                        </div>
                        <ArrowRight className="text-gray-300 group-hover:text-blue-500" />
                      </button>
                    </>
                  )}

                  {storeConfig?.allow_delivery_payment && (
                    <div className="space-y-2">
                      <button 
                        onClick={() => setShowDeliveryOptions(!showDeliveryOptions)} 
                        disabled={loading} 
                        className={`w-full bg-white border-2 p-4 rounded-xl flex items-center justify-between group transition-all shadow-sm ${showDeliveryOptions ? 'border-orange-500 ring-2 ring-orange-100' : 'border-orange-100 hover:border-orange-500'}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center text-orange-600"><Truck size={24} /></div>
                          <div className="text-left"><h3 className="font-bold text-gray-800">Pagar na Entrega</h3><p className="text-xs text-orange-600 font-medium">Escolha como pagar ao receber</p></div>
                        </div>
                        <ArrowRight className={`text-gray-300 transition-transform ${showDeliveryOptions ? 'rotate-90 text-orange-500' : 'group-hover:text-orange-500'}`} />
                      </button>

                      {showDeliveryOptions && (
                        <div className="grid grid-cols-2 gap-2 animate-slide-down pb-2">
                          {[
                            { id: 'pix', label: 'PIX', icon: <QrCode size={16} /> },
                            { id: 'credit', label: 'Cartão Crédito', icon: <CreditCard size={16} /> },
                            { id: 'debit', label: 'Cartão Débito', icon: <CreditCard size={16} /> },
                            { id: 'cash', label: 'Dinheiro', icon: <DollarSign size={16} /> }
                          ].map((opt) => (
                            <button
                              key={opt.id}
                              onClick={() => handlePayment('delivery', opt.id)}
                              className="flex items-center justify-center gap-2 p-3 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-700 hover:border-orange-500 hover:bg-orange-50 transition-all active:scale-95"
                            >
                              {opt.icon} {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {loading && <div className="flex flex-col items-center justify-center py-6 text-emerald-600"><Loader2 className="animate-spin mb-2" size={32} /><p className="text-sm font-medium">Processando pagamento seguro...</p></div>}
                </div>
              )}

              {/* ETAPA 4: PIX GERADO (Mantida) */}
              {step === 'pix_result' && pixData && (
                <div className="flex flex-col items-center animate-fade-in text-center">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4"><Check size={32} strokeWidth={3} /></div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">Pedido Gerado!</h3>
                  <p className="text-gray-600 mb-6">Escaneie o QR Code para pagar <b>R$ {finalAmount.toFixed(2)}</b></p>
                  <div className="bg-white p-4 rounded-xl shadow-inner border border-gray-200 mb-6">
                    <img src={`data:image/jpeg;base64,${pixData.qr_code_base64}`} alt="QR Code Pix" className="w-64 h-64 object-contain" />
                  </div>
                  <div className="w-full relative mb-6">
                    <input type="text" readOnly value={pixData.qr_code} className="w-full pl-4 pr-12 py-3 bg-gray-100 border border-gray-200 rounded-xl text-xs text-gray-500 font-mono truncate" />
                    <button onClick={copyToClipboard} className="absolute right-2 top-2 p-1.5 bg-white shadow-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors">
                      {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                    </button>
                  </div>
                  <button onClick={() => {
                    onClose();
                    if (storePhone) {
                      let cleanPhone = storePhone.replace(/\D/g, '');
                      if (cleanPhone.length <= 11) cleanPhone = '55' + cleanPhone;
                      window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent("Olá! Realizei o pagamento do pedido. Poderiam confirmar? 🚀")}`, '_blank');
                    }
                  }} className="text-emerald-600 font-semibold hover:underline">
                    Fechar e acompanhar pelo WhatsApp
                  </button>
                </div>
              )}

              {/* ETAPA 5: SUCESSO PAGAMENTO NA ENTREGA (NOVO) */}
              {step === 'delivery_success' && (
                <div className="flex flex-col items-center animate-fade-in text-center py-10">
                  <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-emerald-100"><Check size={40} strokeWidth={3} /></div>
                  <h3 className="text-3xl font-black text-gray-800 mb-2">Pedido Confirmado!</h3>
                  <p className="text-gray-600 mb-8 max-w-xs mx-auto">
                    Seu pedido foi registrado com sucesso. 
                    O pagamento de <b>R$ {(orderFinalAmount || finalAmount).toFixed(2)}</b> será realizado no momento da entrega via <b>{
                      deliveryMethod === 'pix' ? 'PIX' : 
                      deliveryMethod === 'credit' ? 'Cartão de Crédito' : 
                      deliveryMethod === 'debit' ? 'Cartão de Débito' : 
                      deliveryMethod === 'cash' ? 'Dinheiro' : 'Entrega'
                    }</b>.
                  </p>
                  
                  <div className="bg-emerald-50 p-6 rounded-2xl border-2 border-dashed border-emerald-200 mb-8 w-full">
                    <p className="text-sm font-bold text-emerald-800 mb-2 flex items-center justify-center gap-2">
                      <MessageCircle size={18} /> Ação Necessária:
                    </p>
                    <p className="text-xs text-emerald-700 leading-relaxed">
                      Para agilizar sua entrega, clique no botão abaixo e <b>envie a confirmação/comprovante do pedido</b> pelo nosso WhatsApp.
                    </p>
                  </div>

                  <button onClick={() => {
                    onClose();
                    if (storePhone) {
                      let cleanPhone = storePhone.replace(/\D/g, '');
                      if (cleanPhone.length <= 11) cleanPhone = '55' + cleanPhone;
                      window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent("Olá! Fiz um pedido com pagamento na entrega. Gostaria de confirmar! 🚀")}`, '_blank');
                    }
                  }} className="w-full bg-emerald-600 text-white font-bold py-4 rounded-xl shadow-xl shadow-emerald-200 flex items-center justify-center gap-2 hover:bg-emerald-700 transition-all active:scale-95">
                    <MessageCircle size={20} /> Confirmar pelo WhatsApp
                  </button>
                </div>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;
